import React, { useState, useEffect, useRef } from 'react';
import '../../../../../style/ElementsStyle.css';

/**
 * ImportacionMasivaModal - Modal con sistema de "Wizard" (pasos).
 * Permite navegar entre diferentes pantallas dentro del mismo modal.
 */
export default function ImportacionMasivaModal({
    open,
    onClose,
    title,
    description,
    steps = [],
    onFinish,
    currentModule,
    triggerAlert
}) {
    const [currentStep, setCurrentStep] = useState(0);
    const [canNext, setCanNext] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [wizardData, setWizardData] = useState({}); // Datos compartidos entre pasos

    // Referencia para interceptar el cambio de paso desde el hijo
    const handleStepChangeRef = useRef(null);

    // Limpiar referencia al cambiar de paso
    useEffect(() => {
        handleStepChangeRef.current = null;
    }, [currentStep]);

    // Reiniciar al paso 0 y habilitar navegación por defecto al abrir el modal
    useEffect(() => {
        if (open) {
            setCurrentStep(0);
            setCanNext(true);
            setIsProcessing(false);
            setWizardData({});
        }
    }, [open]);

    // Importante: No ponemos un useEffect aquí que fuerce setCanNext(true) 
    // porque los hijos (componentes de paso) se encargarán de reportar su estado al montarse.

    if (!open) return null;

    const totalSteps = steps.length;
    const isFirstStep = currentStep === 0;
    const isLastStep = currentStep === totalSteps - 1;

    // Obtener datos del paso actual
    const stepData = steps[currentStep] || {};
    const StepComponent = stepData.component;

    const handleNext = async () => {
        // Si el paso actual tiene un manejador registrado, lo ejecutamos primero
        if (handleStepChangeRef.current) {
            const shouldContinue = await handleStepChangeRef.current();
            if (!shouldContinue) return; // Si retorna false, detenemos el avance
        }

        if (isLastStep) {
            if (onFinish) onFinish();
            onClose();
        } else {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handleBack = () => {
        if (!isFirstStep) {
            setCurrentStep(prev => prev - 1);
        }
    };

    return (
        <div className="import-modal-overlay" onClick={onClose}>
            <div className="import-modal-content" onClick={e => e.stopPropagation()}>
                {/* Header dinámico según el paso */}
                <header className="import-modal-header">
                    <div className="import-modal-header-info">
                        <h2>{stepData.title || title}</h2>
                        <p>{stepData.description || description}</p>
                    </div>
                    <button className="import-modal-close" onClick={onClose} title="Cerrar">
                        <i className="fas fa-times"></i>
                    </button>
                </header>

                {/* Cuerpo del Modal (Contenido del Paso) */}
                <div className="import-modal-body">
                    {StepComponent ? (
                        <StepComponent
                            onNext={handleNext}
                            onBack={handleBack}
                            currentModule={currentModule}
                            setCanNext={setCanNext}
                            setIsProcessing={setIsProcessing}

                            wizardData={wizardData}
                            setWizardData={setWizardData}
                            onStepChange={handleStepChangeRef} // Pasamos la referencia para que el hijo se suscriba
                            triggerAlert={triggerAlert}
                        />
                    ) : (
                        <div className="import-modal-placeholder">
                            <i className="fas fa-magic"></i>
                            <h3>Configuración de Paso</h3>
                            <p>Este paso no tiene un componente asignado todavía.</p>
                        </div>
                    )}
                </div>

                {/* Footer con Navegación tipo Wizard */}
                {totalSteps > 0 && (
                    <footer className="import-modal-footer">
                        {/* Indicador de progreso (Dots) */}
                        <div className="import-step-dots">
                            {steps.map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`step-dot ${idx === currentStep ? 'active' : ''}`}
                                />
                            ))}
                        </div>

                        {/* Botones de acción */}
                        <div className="import-nav-actions">
                            {!isFirstStep && !isLastStep && (
                                <button className="btn-cancel" onClick={handleBack}>
                                    <i className="fas fa-arrow-left"></i> Anterior
                                </button>
                            )}

                            <button
                                className={`btn-action ${(!canNext || isProcessing) ? 'disabled' : ''}`}
                                onClick={handleNext}
                                disabled={!canNext || isProcessing}
                            >
                                {isProcessing ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        {isLastStep ? 'Finalizar' : 'Siguiente'}
                                        {!isLastStep && <i className="fas fa-arrow-right"></i>}
                                    </>
                                )}
                            </button>
                        </div>
                    </footer>
                )}
            </div>
        </div>
    );
}
